import React from 'react';
import { hardReloadToVersion } from './staleClient.js';

// ─── UPDATE-READY PILL ──────────────────────────────────────────────────────
// Haley (2026-07-30): "refreshes only happen on purpose" + "something that
// indicates a new update is available". The version poll in SixGuysRP no
// longer reloads anything — it dispatches 'mfml:update-available' and THIS
// pill is the only path to the refresh: a deliberate tap. Dismissal is
// per-version (a new deploy shows it again). Mounted once in main.jsx so it
// exists on every view of the app.

const DISMISS_KEY = 'mfml_update_pill_dismissed';

export default function UpdatePill({ onReload = hardReloadToVersion }) {
  const [version, setVersion] = React.useState(null);
  const [reloading, setReloading] = React.useState(false);

  React.useEffect(() => {
    const onUpdate = (e) => {
      const v = e?.detail?.version;
      if (!v) return;
      try { if (localStorage.getItem(DISMISS_KEY) === v) return; } catch { /* show it */ }
      setVersion(v);
    };
    window.addEventListener('mfml:update-available', onUpdate);
    return () => window.removeEventListener('mfml:update-available', onUpdate);
  }, []);

  if (!version) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 'calc(env(safe-area-inset-bottom, 0px) + 14px)', left: '50%',
      transform: 'translateX(-50%)', zIndex: 99997,
      display: 'flex', alignItems: 'center', gap: '4px',
      background: 'var(--ds-glass-strong, rgba(16,12,16,0.95))',
      border: '1px solid var(--ds-gold-a4c, rgba(196,168,130,0.3))',
      borderRadius: '999px', padding: '4px 6px 4px 14px',
      boxShadow: 'var(--ds-shadow-2, 0 8px 30px rgba(0,0,0,0.6))',
      backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      animation: 'fadeSlideIn 0.25s ease',
    }}>
      <button
        onClick={() => { setReloading(true); onReload(version); }}
        disabled={reloading}
        style={{
          background: 'none', border: 'none', cursor: reloading ? 'wait' : 'pointer',
          color: 'var(--ds-gold, #c4a882)', fontSize: '14px', fontWeight: 700,
          letterSpacing: '0.5px', padding: '6px 4px', minHeight: '36px',
          display: 'flex', alignItems: 'center', gap: '7px', touchAction: 'manipulation',
        }}
      >
        <span aria-hidden="true" style={{ fontSize: '15px' }}>⟳</span>
        {reloading ? 'Refreshing…' : 'Update ready — tap to refresh'}
      </button>
      <button
        onClick={() => { try { localStorage.setItem(DISMISS_KEY, version); } catch { /* session-only */ } setVersion(null); }}
        aria-label="Dismiss update notice"
        title="Dismiss (shows again on the next update)"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--ds-ink-faint, #8a7b74)', fontSize: '15px', lineHeight: 1,
          padding: '8px 10px', minHeight: '36px', touchAction: 'manipulation',
        }}
      >✕</button>
    </div>
  );
}
